import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { holdIdleExit } from '../../services/idleHold';
import {
    ERZEUGER,
    folgeAuswahl,
    legeFolgeunterlagenAn,
    leseBestaende,
    lesefehlerText,
    type FolgeAuswahlZeile,
    type FolgeBestaende,
} from '../../services/folgeunterlagen/folgeunterlagen';
import type {
    ErzeugungsErgebnis,
    FolgeArt,
    FolgeErzeuger,
    PlanungsQuelle,
} from '../../services/folgeunterlagen/typen';
import type { PflichtEintrag } from './types';

export interface FolgeunterlagenStand {
    /** Die vier Zeilen der Auswahl (`folgeAuswahl`) — immer vier, ungelesen „wird geprüft …". */
    zeilen: FolgeAuswahlZeile[];
    /** Die Bestaende werden gerade gelesen. */
    laden: boolean;
    /** Satz ueber nicht lesbare Bestaende, sonst `null`. */
    lesefehler: string | null;
    neuLesen: () => void;
    anlageLaeuft: boolean;
    /** Ergebnis der letzten Anlage dieses Projekts in dieser Sitzung. */
    ergebnisse: ErzeugungsErgebnis[] | null;
    /** Legt die gewaehlten Unterlagen nacheinander an. In der Nur-Ansicht,
     *  waehrend einer Anlage oder ohne Auswahl: nichts, leere Liste. */
    legeAn: (arten: readonly FolgeArt[], q: PlanungsQuelle) => Promise<ErzeugungsErgebnis[]>;
}

/**
 * Folgeunterlagen der Planungsphase im Container (`PlanungModul`).
 *
 * Liest die vier Bestaende NUR, wenn `aktiv` (die Planung ist erfuellt oder
 * abgeschlossen) — nicht bei jedem Oeffnen des Moduls. Nach jeder Anlage
 * und auf `neuLesen` wird neu gelesen.
 *
 * ═══ LAUFSPERRE ═══════════════════════════════════════════════════════════
 * Der SiGe-Plan hat keinen Unique-Index je Projekt; eine zweite Anlage
 * parallel (Doppelklick, Karte und Abschluss zugleich) koennte zwei Plaene
 * anlegen. Die Sperre ist ein Ref — `anlageLaeuft` als Zustand griffe erst
 * nach dem naechsten Zeichnen. Waehrenddessen haelt der Leerlaufrueckzug
 * still, damit das Projekt nicht mitten im Schreiben schliesst.
 *
 * ═══ OHNE SETSTATE IM EFFEKT ═════════════════════════════════════════════
 * `laden` ist abgeleitet: gelesen ist, was zum aktuellen Schluessel (Projekt
 * und Lesedurchgang) passt. Der Effekt setzt nur im Rueckruf des Lesens.
 */
export const useFolgeunterlagen = ({
    projectId,
    pflichten,
    aktiv,
    isReadOnly,
    erzeuger = ERZEUGER,
}: {
    projectId: string;
    pflichten: readonly PflichtEintrag[] | null;
    aktiv: boolean;
    isReadOnly: boolean;
    /** Nur fuer Doubles — sonst die vier echten Erzeuger. */
    erzeuger?: Record<FolgeArt, FolgeErzeuger>;
}): FolgeunterlagenStand => {
    // Jeder Lesedurchgang hat einen Schluessel; ein spaetes Ergebnis eines
    // aelteren Durchgangs oder Projekts zaehlt nicht.
    const [durchgang, setDurchgang] = useState(0);
    const schluessel = `${projectId}#${durchgang}`;
    const [gelesen, setGelesen] = useState<{ fuer: string; bestaende: FolgeBestaende } | null>(null);

    useEffect(() => {
        if (!aktiv) return;
        let gilt = true;
        void leseBestaende(projectId, erzeuger).then(bestaende => {
            if (gilt) setGelesen({ fuer: schluessel, bestaende });
        });
        return () => { gilt = false; };
    }, [aktiv, projectId, schluessel, erzeuger]);

    const bestaende = gelesen && gelesen.fuer === schluessel ? gelesen.bestaende : null;
    const laden = aktiv && !bestaende;
    const zeilen = useMemo(() => folgeAuswahl(pflichten, bestaende), [pflichten, bestaende]);
    const lesefehler = useMemo(() => lesefehlerText(bestaende, erzeuger), [bestaende, erzeuger]);

    const neuLesen = useCallback(() => setDurchgang(n => n + 1), []);

    // Zweite Reihe des Schreibschutzes: `isReadOnly` kann zur Laufzeit
    // kippen, waehrend ein Aufrufer noch eine alte `legeAn` haelt (Abschluss
    // → erst nach dem Neuladen anlegen).
    const nurAnsicht = useRef(isReadOnly);
    useEffect(() => { nurAnsicht.current = isReadOnly; }, [isReadOnly]);

    const eingehaengt = useRef(true);
    useEffect(() => {
        eingehaengt.current = true;
        return () => { eingehaengt.current = false; };
    }, []);

    const laufRef = useRef(false);
    const [anlageLaeuft, setAnlageLaeuft] = useState(false);
    const [ergebnis, setErgebnis] = useState<{ fuer: string; liste: ErzeugungsErgebnis[] } | null>(null);

    const legeAn = useCallback(async (arten: readonly FolgeArt[], q: PlanungsQuelle): Promise<ErzeugungsErgebnis[]> => {
        if (nurAnsicht.current || laufRef.current || !arten.length) return [];
        laufRef.current = true;
        setAnlageLaeuft(true);
        const freigeben = holdIdleExit('folgeunterlagen-anlage');
        try {
            const liste = await legeFolgeunterlagenAn(arten, q, erzeuger);
            if (eingehaengt.current) setErgebnis({ fuer: q.projectId, liste });
            return liste;
        } finally {
            freigeben();
            laufRef.current = false;
            if (eingehaengt.current) {
                setAnlageLaeuft(false);
                setDurchgang(n => n + 1);
            }
        }
    }, [erzeuger]);

    return {
        zeilen,
        laden,
        lesefehler,
        neuLesen,
        anlageLaeuft,
        ergebnisse: ergebnis && ergebnis.fuer === projectId ? ergebnis.liste : null,
        legeAn,
    };
};
